import { action } from '@ember/object';
import Component from '@glimmer/component';

import {
	getVerificationEntityDisplayName,
	getVerifiedActor,
	getVerifiedDate,
	isVerified,
} from 'webapp/utils/verification';
import type { VerificationEntityLabel, VerificationInfo } from 'webapp/utils/verification';

import AvatarOrInitials from 'webapp/components/avatar-or-initials';
import CommonUiIcon from 'webapp/components/common/ui-icon';
import FormCheckboxSwitch from 'webapp/components/form/checkbox-switch';
import ToolTipWithIcon from 'webapp/components/tool-tip-with-icon';

interface Args {
	verification?: VerificationInfo;
	entityLabel: VerificationEntityLabel;
	onToggle: () => void;
	isDisabled?: boolean;
}

export default class VerificationToggleComponent extends Component<Args> {
	get verification(): VerificationInfo | undefined {
		return this.args.verification;
	}

	get isVerified(): boolean {
		return isVerified(this.verification);
	}

	get verifiedByName(): string {
		return getVerifiedActor(this.verification)?.name ?? 'Unknown user';
	}

	get verifiedByAvatar(): string | undefined {
		return getVerifiedActor(this.verification)?.avatar ?? undefined;
	}

	get verifiedDate(): string {
		return getVerifiedDate(this.verification, 'MMM D, YYYY [at] h:mma');
	}

	get label(): string {
		return `Verified ${getVerificationEntityDisplayName(this.args.entityLabel)}`;
	}

	get iconClass(): string {
		return this.isVerified ? 'verification-toggle__icon--active' : 'verification-toggle__icon';
	}

	get tooltipText(): string {
		return `Mark this ${getVerificationEntityDisplayName(this.args.entityLabel)} as verified to indicate it has been reviewed and approved.`;
	}

	@action
	toggle(): void {
		this.args.onToggle();
	}

	<template>
		{{#if @verification}}
			<div class="verification-toggle">
				<div class="verification-toggle__header">
					<CommonUiIcon
						@name={{if this.isVerified "VerifiedFill" "Verified"}}
						@size="md"
						@class={{this.iconClass}}
					/>
					<span class="verification-toggle__label">{{this.label}}</span>
					<ToolTipWithIcon
						@text={{this.tooltipText}}
						@position="top"
						@iconName="InfoFill"
						@iconSize="xs"
					/>
					<FormCheckboxSwitch
						class="verification-toggle__switch"
						@name="verification-toggle"
						@selected={{this.isVerified}}
						@onChange={{this.toggle}}
						@isDisabled={{@isDisabled}}
					/>
				</div>

				{{#if this.isVerified}}
					<hr class="verification-toggle__divider" />
					<div class="verification-toggle__details">
						<div class="verification-toggle__row">
							<span class="verification-toggle__detail-label">Verified by</span>
							<div class="verification-toggle__detail-value">
								<AvatarOrInitials
									@name={{this.verifiedByName}}
									@avatar={{this.verifiedByAvatar}}
								/>
								<span>{{this.verifiedByName}}</span>
							</div>
						</div>
						<div class="verification-toggle__row">
							<span class="verification-toggle__detail-label">Date</span>
							<span class="verification-toggle__detail-value">
								{{this.verifiedDate}}
							</span>
						</div>
					</div>
				{{/if}}
			</div>
		{{/if}}
	</template>
}
